import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  Box,
  Divider,
  Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import apiClient from '../../networking/axiosconfig';
import { useSelector } from 'react-redux';

const AddCategory = ({ open, onClose, onSave, category, isEdit }) => {
  const { data: config } = useSelector((state) => state.config);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [priceRanges, setPriceRanges] = useState([]);
  const [newRange, setNewRange] = useState({ min: '', max: '', label: '' });
  const [loading, setLoading] = useState(false);
  // Filters state
  const [filters, setFilters] = useState([]);
  const [newFilter, setNewFilter] = useState({ key: '', label: '', icon: '', values: [] });
  const [newFilterValue, setNewFilterValue] = useState('');

  useEffect(() => {
    if (category) {
      setName(category.name || '');
      setIcon(category.icon || '');
      setSortOrder(category.sortOrder || 0);
      setPriceRanges(category.priceRanges || []);
      setFilters(category.filters || []);
    } else {
      resetForm();
    }
  }, [category, open]);

  const resetForm = () => {
    setName('');
    setIcon('');
    setSortOrder(0);
    setPriceRanges([]);
    setNewRange({ min: '', max: '', label: '' });
    setFilters([]);
    setNewFilter({ key: '', label: '', icon: '', values: [] });
    setNewFilterValue('');
  };
  // Filter management
  const handleAddFilterValue = () => {
    if (!newFilterValue) return;
    setNewFilter({
      ...newFilter,
      values: [
        ...newFilter.values,
        { value: newFilterValue } // do not send _id for new values
      ]
    });
    setNewFilterValue('');
  };

  const handleRemoveFilterValue = (idx) => {
    setNewFilter({
      ...newFilter,
      values: newFilter.values.filter((_, i) => i !== idx)
    });
  };

  const handleAddFilter = () => {
    if (!newFilter.key || !newFilter.label) return;
    setFilters([...filters, newFilter]);
    setNewFilter({ key: '', label: '', icon: '', values: [] });
    setNewFilterValue('');
  };

  const handleRemoveFilter = (idx) => {
    setFilters(filters.filter((_, i) => i !== idx));
  };

  // Editing filter fields
  const handleEditFilterField = (idx, field, value) => {
    setFilters(filters.map((filter, i) => i === idx ? { ...filter, [field]: value } : filter));
  };

  // Editing filter value
  const handleEditFilterValue = (filterIdx, valueIdx, newValue) => {
    setFilters(filters.map((filter, i) =>
      i === filterIdx
        ? { ...filter, values: filter.values.map((val, vi) => vi === valueIdx ? { ...val, value: newValue } : val) }
        : filter
    ));
  };

  // Remove a value from a filter
  const handleRemoveFilterValueFromFilter = (filterIdx, valueIdx) => {
    setFilters(filters.map((filter, i) =>
      i === filterIdx
        ? { ...filter, values: filter.values.filter((_, vi) => vi !== valueIdx) }
        : filter
    ));
  };

  const handleAddPriceRange = () => {
    const min = parseFloat(newRange.min);
    const max = parseFloat(newRange.max);
    setPriceRanges([...priceRanges, { min, max, label: newRange.label }]);
    setNewRange({ min: '', max: '', label: '' });
  };

  const handleRemovePriceRange = (index) => {
    const updatedRanges = [...priceRanges];
    updatedRanges.splice(index, 1);
    setPriceRanges(updatedRanges);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const categoryData = {
        name,
        icon,
        sortOrder: parseInt(sortOrder) || 0,
        priceRanges: priceRanges.map(range => ({
          min: parseFloat(range.min),
          max: parseFloat(range.max),
          label: range.label
        })),
        filters: filters
      };

      if (isEdit && category) {
        await apiClient.put(`${config.API_BASE_URL}/api/categories/${category._id}`, categoryData);
      } else {
        await apiClient.post(`${config.API_BASE_URL}/api/categories`, categoryData);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Category' : 'Add New Category'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
            {/* ...existing code for price ranges... */}
          </Grid>
          {/* Filters section moved to bottom */}

              <TextField
                label="Category Name"
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Icon Filename"
                fullWidth
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                margin="normal"
                placeholder="icon-name.png"
                helperText="Enter the filename of the icon (e.g., electronics.png). The file should be in the /images/ directory."
              />
              {icon && (
                <Box mt={1} display="flex" alignItems="center">
                  <Typography variant="caption" color="textSecondary">
                    Icon Preview:
                  </Typography>
                  <img 
                    src={`${config.API_BASE_URL}/images/${icon}`} 
                    alt="Icon preview" 
                    style={{ 
                      width: '30px', 
                      height: '30px', 
                      marginLeft: '10px',
                      objectFit: 'contain' 
                    }} 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sort Order"
                type="number"
                fullWidth
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                margin="normal"
              />
            </Grid>


            <Grid item xs={12}>
              <Divider style={{ margin: '20px 0' }}>
                <Typography variant="subtitle1">Price Ranges</Typography>
              </Divider>
              
              <Box mb={2}>
                {priceRanges.map((range, index) => (
                  <Chip
                    key={index}
                    label={`${range.label} (${range.min} - ${range.max})`}
                    onDelete={() => handleRemovePriceRange(index)}
                    style={{ margin: '0 8px 8px 0' }}
                    deleteIcon={<DeleteIcon />}
                  />
                ))}
              </Box>

              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Min Price"
                    type="number"
                    fullWidth
                    value={newRange.min}
                    onChange={(e) => setNewRange({...newRange, min: e.target.value})}
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Max Price"
                    type="number"
                    fullWidth
                    value={newRange.max}
                    onChange={(e) => setNewRange({...newRange, max: e.target.value})}
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Label"
                    fullWidth
                    value={newRange.label}
                    onChange={(e) => setNewRange({...newRange, label: e.target.value})}
                    margin="dense"
                    placeholder="e.g., Budget"
                  />
                </Grid>
                <Grid item xs={12} sm={1}>
                  <IconButton 
                    color="primary" 
                    onClick={handleAddPriceRange}
                    disabled={!newRange.min || !newRange.max || !newRange.label}
                  >
                    <AddIcon />
                  </IconButton>
                </Grid>
              </Grid>

            </Grid>
        </DialogContent>
        {/* Filters section moved to bottom, after DialogContent but before DialogActions */}
        <Divider style={{ margin: '20px 0' }}>
          <Typography variant="subtitle1">Filters</Typography>
        </Divider>
        <Box mb={2}>
          {filters.map((filter, idx) => (
            <Box key={idx} mb={2} p={2} border={1} borderRadius={2} borderColor="grey.300">
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    label="Label"
                    value={filter.label}
                    onChange={e => handleEditFilterField(idx, 'label', e.target.value)}
                    size="small"
                    style={{ width: 120 }}
                  />
                  <TextField
                    label="Key"
                    value={filter.key}
                    onChange={e => handleEditFilterField(idx, 'key', e.target.value)}
                    size="small"
                    style={{ width: 120 }}
                  />
                  <TextField
                    label="Icon"
                    value={filter.icon}
                    onChange={e => handleEditFilterField(idx, 'icon', e.target.value)}
                    size="small"
                    style={{ width: 120 }}
                  />
                </Box>
                <IconButton size="small" onClick={() => handleRemoveFilter(idx)}><DeleteIcon /></IconButton>
              </Box>
              <Box mt={1} display="flex" flexWrap="wrap" alignItems="center">
                {filter.values.map((val, vIdx) => (
                  <Box key={val._id} display="flex" alignItems="center" mr={1} mb={1}>
                    <TextField
                      value={val.value}
                      onChange={e => handleEditFilterValue(idx, vIdx, e.target.value)}
                      size="small"
                      style={{ width: 100 }}
                    />
                    <IconButton size="small" onClick={() => handleRemoveFilterValueFromFilter(idx, vIdx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {/* Add new value to existing filter */}
                <TextField
                  label="New Value"
                  value={filter.newValue || ''}
                  onChange={e => {
                    const val = e.target.value;
                    setFilters(filters.map((f, i) => i === idx ? { ...f, newValue: val } : f));
                  }}
                  size="small"
                  style={{ minWidth: 100 }}
                />
                <IconButton
                  color="primary"
                  onClick={() => {
                    if (!filter.newValue) return;
                    setFilters(filters.map((f, i) =>
                      i === idx
                        ? {
                            ...f,
                            values: [...f.values, { value: f.newValue }], // do not send _id for new values
                            newValue: ''
                          }
                        : f
                    ));
                  }}
                  disabled={!filter.newValue}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
        <Box mb={2} p={2} border={1} borderRadius={2} borderColor="grey.200">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                label="Filter Key"
                fullWidth
                value={newFilter.key}
                onChange={e => setNewFilter({ ...newFilter, key: e.target.value })}
                margin="dense"
                placeholder="e.g., screenSize"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Filter Label"
                fullWidth
                value={newFilter.label}
                onChange={e => setNewFilter({ ...newFilter, label: e.target.value })}
                margin="dense"
                placeholder="e.g., Screen Size"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Filter Icon"
                fullWidth
                value={newFilter.icon}
                onChange={e => setNewFilter({ ...newFilter, icon: e.target.value })}
                margin="dense"
                placeholder="e.g., tv, mobile, camera"
              />
            </Grid>
          </Grid>
          <Box mt={2}>
            <Typography variant="subtitle2">Filter Values</Typography>
            <Box display="flex" flexWrap="wrap" alignItems="center">
              {newFilter.values.map((val, idx) => (
                <Chip
                  key={val._id}
                  label={val.value}
                  onDelete={() => handleRemoveFilterValue(idx)}
                  style={{ margin: '0 8px 8px 0' }}
                />
              ))}
              <TextField
                label="New Value"
                value={newFilterValue}
                onChange={e => setNewFilterValue(e.target.value)}
                margin="dense"
                style={{ minWidth: 120 }}
              />
              <IconButton color="primary" onClick={handleAddFilterValue} disabled={!newFilterValue}>
                <AddIcon />
              </IconButton>
            </Box>
            <Box mt={2}>
              <Button
                color="primary"
                variant="contained"
                onClick={handleAddFilter}
                disabled={!newFilter.key || !newFilter.label}
              >
                Add Filter
              </Button>
            </Box>
          </Box>
        </Box>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            color="primary" 
            variant="contained" 
            disabled={!name || loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddCategory;
